import * as React from 'react';

import type { TypeaheadOption } from '@/components/ui/typeahead';

/**
 * The shared half of every typeahead's DATA, beside the shared half of its
 * rendering (`components/ui/typeahead.tsx`).
 *
 * A picker is two reads and one piece of state, and getting any of the three
 * wrong is a defect rather than a preference — so they live here once:
 *
 *  - the QUERY, debounced. A keystroke is not a request: 50,000 items behind
 *    an ILIKE is 9 ms of database work (see PAGINATION.md), and typing
 *    "stapler" would otherwise ask for it seven times, each answer racing the
 *    last. 200 ms is under the ~250 ms at which a reader notices a pause and
 *    long enough that a typed word is one request.
 *  - the SEARCH results, which are one page and never the list.
 *  - the HELD records, resolved by id, which is what keeps a saved line's
 *    value on screen when it is not in the current results.
 */

/** The value, one tick behind the keystrokes. */
export function useDebounced<T>(value: T, ms = 200): T {
  const [settled, setSettled] = React.useState(value);
  React.useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

export interface PickerState<Row> {
  /** What the user has typed, and its setter — straight onto `<Typeahead>`. */
  query: string;
  setQuery: (query: string) => void;
  /** Debounced; the caller's search hook takes this, never `query`. */
  search: string;
  /** One page of matches, as rows to draw. */
  options: TypeaheadOption[];
  /** Every record in play — the matches AND the ones the form holds — by id. */
  byId: Map<string, Row>;
  /** The option for an id the form holds, whether or not the search found it. */
  optionFor: (id: string | null | undefined) => TypeaheadOption | null;
  loading: boolean;
}

/**
 * Fold one page of matches and the held records into what `<Typeahead>` and
 * its editor both need.
 *
 * `byId` carries the WHOLE record rather than the option, because picking a
 * line does more than set an id: BR-MD-03-14 fills the description and the
 * price from the item, OQ-05-17 asks it for its tracking blocks, and N-4 asks
 * its unit for a precision. A picker that returned only `{id, label}` would
 * make every editor fetch the record again on pick.
 */
export function usePickerState<Row extends { id: string }>(input: {
  query: string;
  setQuery: (query: string) => void;
  search: string;
  matches: readonly Row[] | undefined;
  held: readonly Row[] | undefined;
  toOption: (row: Row) => TypeaheadOption;
  loading: boolean;
}): PickerState<Row> {
  const { matches, held, toOption } = input;
  const byId = React.useMemo(() => {
    const map = new Map<string, Row>();
    for (const row of held ?? []) map.set(row.id, row);
    for (const row of matches ?? []) map.set(row.id, row);
    return map;
  }, [held, matches]);

  const options = React.useMemo(() => (matches ?? []).map(toOption), [matches, toOption]);

  const optionFor = React.useCallback(
    (id: string | null | undefined): TypeaheadOption | null => {
      if (!id) return null;
      const row = byId.get(id);
      return row === undefined ? null : toOption(row);
    },
    [byId, toOption],
  );

  return {
    query: input.query,
    setQuery: input.setQuery,
    search: input.search,
    options,
    byId,
    optionFor,
    loading: input.loading,
  };
}
